import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { AppDrawer } from '../components/ui/AppDrawer';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Loader2, Truck, RefreshCw, AlertTriangle, AlertCircle, CheckCircle2,
  Package, Clock, ClipboardList,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { predictiveRestockAPI } from '../lib/v2Api';
import { VanRestockReviewDrawer } from '../components/VanRestockReviewDrawer';

const STATUS_CONFIG = {
  RED: { color: 'bg-red-500', ring: 'ring-red-200', bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', icon: AlertCircle, label: 'Critical' },
  YELLOW: { color: 'bg-amber-400', ring: 'ring-amber-200', bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', icon: AlertTriangle, label: 'At Risk' },
  GREEN: { color: 'bg-emerald-500', ring: 'ring-emerald-200', bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', icon: CheckCircle2, label: 'Healthy' },
};

const VehicleCard = ({ vehicle, onClick }) => {
  const cfg = STATUS_CONFIG[vehicle.status] || STATUS_CONFIG.GREEN;
  const StatusIcon = cfg.icon;

  return (
    <button
      onClick={() => onClick(vehicle)}
      className={`w-full text-left p-4 rounded-lg border ${cfg.border} hover:shadow-md transition-all group`}
      data-testid={`vehicle-card-${vehicle.van_id}`}
    >
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-8 h-8 rounded-full ${cfg.bg} flex items-center justify-center`}>
          <StatusIcon className={`w-4 h-4 ${cfg.text}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm truncate">{vehicle.van_name}</p>
        </div>
        <Badge className={`text-[10px] ${cfg.bg} ${cfg.text} border ${cfg.border}`}>
          {cfg.label}
        </Badge>
      </div>
      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <Package className="w-3 h-3" /> {vehicle.total_items} item{vehicle.total_items !== 1 ? 's' : ''}
        </span>
        {vehicle.items_out_of_stock > 0 && (
          <span className="flex items-center gap-1 text-red-600 font-medium">
            <AlertCircle className="w-3 h-3" /> {vehicle.items_out_of_stock} out
          </span>
        )}
        {vehicle.items_at_risk > 0 && (
          <span className="flex items-center gap-1 text-amber-600 font-medium">
            <AlertTriangle className="w-3 h-3" /> {vehicle.items_at_risk} at risk
          </span>
        )}
      </div>
    </button>
  );
};

const FleetHealth = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState([]);
  const [restockVan, setRestockVan] = useState(null); // Stage 11A
  const navigate = useNavigate();

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/fleet/health');
      setData(res.data);
    } catch {
      toast.error('Failed to load fleet health');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchHealth(); }, [fetchHealth]);

  const handleVehicleClick = async (vehicle) => {
    setSelectedVehicle(vehicle);
    setDetailLoading(true);
    try {
      const res = await predictiveRestockAPI.getPlan({
        mode: 'hybrid',
        source_type: 'vehicle',
        source_id: vehicle.van_id,
      });
      setDetailData(res.data || []);
    } catch {
      toast.error('Failed to load vehicle details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleGenerateRestock = (vehicle) => {
    setSelectedVehicle(null);
    setRestockVan(vehicle || selectedVehicle);
  };

  const summary = data?.summary || { green: 0, yellow: 0, red: 0 };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
              <Truck className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold" data-testid="fleet-health-title">Fleet Health</h1>
              <p className="text-sm text-muted-foreground">Inventory health across your vehicle fleet</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchHealth} disabled={loading}>
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4" data-testid="fleet-summary">
          {[
            { key: 'red', label: 'Critical', count: summary.red, cfg: STATUS_CONFIG.RED },
            { key: 'yellow', label: 'At Risk', count: summary.yellow, cfg: STATUS_CONFIG.YELLOW },
            { key: 'green', label: 'Healthy', count: summary.green, cfg: STATUS_CONFIG.GREEN },
          ].map(({ key, label, count, cfg }) => {
            const Icon = cfg.icon;
            return (
              <Card key={key} className={`${cfg.border} border`} data-testid={`summary-${key}`}>
                <CardContent className="p-4 flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full ${cfg.bg} flex items-center justify-center`}>
                    <Icon className={`w-5 h-5 ${cfg.text}`} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{count}</p>
                    <p className="text-xs text-muted-foreground">{label}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Vehicle Cards */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : !data?.vehicles?.length ? (
          <Card>
            <CardContent className="py-16 text-center">
              <Truck className="w-12 h-12 mx-auto text-muted-foreground mb-4 opacity-50" />
              <h3 className="font-medium text-lg">No vehicles in fleet</h3>
              <p className="text-sm text-muted-foreground mt-1">Add vehicles to start tracking fleet health</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="vehicle-grid">
            {data.vehicles.map(v => (
              <VehicleCard key={v.van_id} vehicle={v} onClick={handleVehicleClick} />
            ))}
          </div>
        )}
      </div>

      {/* Vehicle Detail Drawer */}
      <AppDrawer
        open={!!selectedVehicle}
        onOpenChange={() => setSelectedVehicle(null)}
        title={
          <span className="flex items-center gap-2">
            <Truck className="w-5 h-5" />
            {selectedVehicle?.van_name}
            {selectedVehicle && (
              <Badge className={`ml-1 text-xs ${STATUS_CONFIG[selectedVehicle.status]?.bg} ${STATUS_CONFIG[selectedVehicle.status]?.text} border ${STATUS_CONFIG[selectedVehicle.status]?.border}`}>
                {STATUS_CONFIG[selectedVehicle.status]?.label}
              </Badge>
            )}
          </span>
        }
        description="Predicted shortages based on usage velocity and stock targets"
        className="sm:max-w-2xl"
        data-testid="vehicle-detail-drawer"
        footer={
          <>
            <Button variant="outline" onClick={() => setSelectedVehicle(null)}>Close</Button>
            <Button onClick={() => handleGenerateRestock()} data-testid="generate-restock-plan-btn">
              <ClipboardList className="w-4 h-4 mr-2" />
              Generate Restock Plan
            </Button>
          </>
        }
      >
        {detailLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : detailData.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground">
            <Package className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <p>No inventory items on this vehicle</p>
          </div>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <Table data-testid="vehicle-detail-table">
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>Item</TableHead>
                  <TableHead className="text-right">On Hand</TableHead>
                  <TableHead className="text-right">Usage/Day</TableHead>
                  <TableHead className="text-right">Days Left</TableHead>
                  <TableHead className="text-right">Suggest</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {detailData.map(row => (
                  <TableRow key={`${row.item_id}-${row.source_id}`} data-testid={`detail-row-${row.item_id}`}>
                    <TableCell>
                      <div>
                        <p className="font-medium text-sm">{row.item_name}</p>
                        {row.item_sku && <p className="text-xs text-muted-foreground">{row.item_sku}</p>}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">{row.qty_on_hand}</TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {row.avg_daily_usage > 0 ? row.avg_daily_usage.toFixed(1) : '\u2014'}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {row.days_until_empty !== null ? (
                        <span className={row.days_until_empty < 7 ? 'text-red-600 font-semibold' : ''}>
                          {row.days_until_empty}d
                        </span>
                      ) : '\u2014'}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {row.suggested_restock_qty > 0 ? (
                        <span className="font-semibold text-indigo-700">{row.suggested_restock_qty}</span>
                      ) : '0'}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={row.status === 'RESTOCK_NEEDED' ? 'destructive' : 'secondary'}
                        className="text-[10px]"
                      >
                        {row.status === 'RESTOCK_NEEDED' ? 'RESTOCK' : 'OK'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </AppDrawer>

      {/* Stage 11 — Restock Review Drawer */}
      <VanRestockReviewDrawer
        open={!!restockVan}
        onOpenChange={(v) => { if (!v) setRestockVan(null); }}
        vanId={restockVan?.van_id}
        vanName={restockVan?.van_name}
      />
    </Layout>
  );
};

export default FleetHealth;
